import type { StoreCustomer } from "@hobbyshopify/backend-client";
import { ChangeNotifier } from "subtree.js/core";
import type { LoadStatus } from "../primitives/load-status";

/**
 * The shop's customers with a valid scan, as last fetched. Cached across pages
 * for the same reason as the review queue; `markStale` after an approval, since
 * a customer whose last scan was answered may drop off the list.
 */
export class CustomersRepository extends ChangeNotifier {
  #status: LoadStatus = { kind: "idle" };
  #customers: readonly StoreCustomer[] = [];
  #stale = false;

  get status(): LoadStatus {
    return this.#status;
  }

  get customers(): readonly StoreCustomer[] {
    return this.#customers;
  }

  get isStale(): boolean {
    return this.#stale;
  }

  find(id: string): StoreCustomer | undefined {
    return this.#customers.find((c) => c.id === id);
  }

  setLoading(): void {
    this.#stale = false;
    this.#set({ kind: "loading" });
  }

  setCustomers(customers: readonly StoreCustomer[]): void {
    this.#customers = [...customers];
    this.#set({ kind: "loaded" });
  }

  /** A customer the shop just added: listed at once, though they have no scans yet. */
  add(customer: StoreCustomer): void {
    this.#customers = [customer, ...this.#customers.filter((c) => c.id !== customer.id)];
    this.notifyListeners();
  }

  setError(message: string): void {
    this.#set({ kind: "error", message });
  }

  markStale(): void {
    if (this.#stale) return;
    this.#stale = true;
    this.notifyListeners();
  }

  #set(status: LoadStatus): void {
    this.#status = status;
    this.notifyListeners();
  }
}
